import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'

export default function PrivacyPage() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | G6 Optics</title>
        <meta name="description" content="Privacy Policy for G6 Optics. Learn how we collect, use, and protect your personal information when you visit our mini camera enthusiast website." />
        <link rel="canonical" href="https://g6optics.com/privacy" />
        <meta name="robots" content="noindex" />
      </Helmet>

      <article className="legal-page">
        <div className="container">
          <header className="legal-header">
            <h1>Privacy Policy</h1>
            <p className="legal-subtitle">How we collect, use, and protect your information</p>
          </header>

          <div className="legal-content">
            <section className="legal-section">
              <h2>Introduction</h2>
              <p>
                G6 Optics ("we," "our," or "us") operates the website g6optics.com (the "Site"). This Privacy 
                Policy explains how we collect, use, disclose, and safeguard your information when you visit 
                our website. Please read this privacy policy carefully.
              </p>
              <p>
                By using the Site, you consent to the data practices described in this policy. If you do not 
                agree with the terms of this privacy policy, please do not access the Site.
              </p>
            </section>

            <section className="legal-section">
              <h2>Information We Collect</h2>
              
              <h3>Automatically Collected Information</h3>
              <p>
                When you visit our Site, certain information is automatically collected, including:
              </p>
              <ul className="legal-list">
                <li>Your IP address and approximate geographic location</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Referring website addresses</li>
                <li>Pages viewed and time spent on pages</li>
                <li>Date and time of your visit</li>
                <li>Device type (mobile, desktop, tablet)</li>
              </ul>

              <h3>Information from Third-Party Services</h3>
              <p>
                We use third-party services that may collect information about your visit:
              </p>
              <ul className="legal-list">
                <li>
                  <strong>Google Analytics:</strong> We use Google Analytics to understand how visitors 
                  interact with our Site. Google Analytics uses cookies to collect anonymous data about 
                  your browsing behavior. You can opt out of Google Analytics by installing the 
                  <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer"> Google Analytics Opt-out Browser Add-on</a>.
                </li>
                <li>
                  <strong>Amazon Associates:</strong> When you click on affiliate links, Amazon may place 
                  cookies to track your purchase for commission purposes. Please review 
                  <a href="https://www.amazon.com/gp/help/customer/display.html?nodeId=468496" target="_blank" rel="noopener noreferrer"> Amazon's Privacy Notice</a> for more information.
                </li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>How We Use Your Information</h2>
              <p>
                We use the information we collect to:
              </p>
              <ul className="legal-list">
                <li>Analyze website traffic and usage patterns to improve our content</li>
                <li>Understand which pages and topics are most popular</li>
                <li>Identify technical issues and improve site performance</li>
                <li>Comply with legal obligations</li>
              </ul>
              <p>
                We do not sell, rent, or share your personal information with third parties for their 
                marketing purposes.
              </p>
            </section>

            <section className="legal-section">
              <h2>Cookies and Tracking Technologies</h2>
              <p>
                Our Site uses cookies and similar tracking technologies. Cookies are small text files 
                stored on your device that help us improve your experience. We use:
              </p>
              <ul className="legal-list">
                <li>
                  <strong>Essential Cookies:</strong> Required for basic site functionality
                </li>
                <li>
                  <strong>Analytics Cookies:</strong> Help us understand how visitors use our Site 
                  (via Google Analytics)
                </li>
                <li>
                  <strong>Affiliate Cookies:</strong> Track purchases made through our affiliate links 
                  (via Amazon Associates)
                </li>
              </ul>
              <p>
                You can control cookies through your browser settings. Note that disabling cookies may 
                affect your ability to use certain features of the Site.
              </p>
            </section>

            <section className="legal-section">
              <h2>Third-Party Links</h2>
              <p>
                Our Site contains links to third-party websites, including Amazon.com. We are not 
                responsible for the privacy practices or content of these external sites. We encourage 
                you to review the privacy policies of any third-party sites you visit.
              </p>
            </section>

            <section className="legal-section">
              <h2>Data Security</h2>
              <p>
                We implement reasonable security measures to protect your information. However, no 
                method of transmission over the Internet is 100% secure. While we strive to protect 
                your personal information, we cannot guarantee its absolute security.
              </p>
            </section>

            <section className="legal-section">
              <h2>Children's Privacy</h2>
              <p>
                Our Site is not intended for children under 13 years of age. We do not knowingly collect 
                personal information from children under 13. If you believe we have collected information 
                from a child under 13, please contact us immediately.
              </p>
            </section>

            <section className="legal-section">
              <h2>Your Rights</h2>
              <p>
                Depending on your location, you may have certain rights regarding your personal information, 
                including:
              </p>
              <ul className="legal-list">
                <li>The right to access the personal information we hold about you</li>
                <li>The right to request correction of inaccurate information</li>
                <li>The right to request deletion of your information</li>
                <li>The right to opt out of certain data collection practices</li>
              </ul>
              <p>
                To exercise these rights, please contact us using the information provided below.
              </p>
            </section>

            <section className="legal-section">
              <h2>California Privacy Rights</h2>
              <p>
                If you are a California resident, you have additional rights under the California Consumer 
                Privacy Act (CCPA). You have the right to request information about the categories of 
                personal information we collect and how it is used.
              </p>
            </section>

            <section className="legal-section">
              <h2>International Users</h2>
              <p>
                If you are accessing our Site from outside the United States, please be aware that your 
                information may be transferred to, stored, and processed in the United States. By using 
                our Site, you consent to this transfer.
              </p>
            </section>

            <section className="legal-section">
              <h2>Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new Privacy Policy on this page and updating the "Last Updated" date below. 
                Your continued use of the Site after any changes constitutes acceptance of the new policy.
              </p>
            </section>

            <section className="legal-section">
              <h2>Contact Us</h2>
              <p>
                If you have questions or concerns about this Privacy Policy, please contact us through 
                the methods available on our <Link to="/about">About page</Link>.
              </p>
            </section>

            <footer className="legal-footer">
              <p>Last updated: January 2026</p>
            </footer>
          </div>
        </div>
      </article>
    </>
  )
}
